import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Spinner, Pagination } from 'react-bootstrap';
import { getJobs, deleteJob } from '../services/api';

class Jobs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      jobs: [],
      loading: true,
      error: null,
      currentPage: 1,
      totalJobs: 0,
      jobsPerPage: 25,
    };
  }

  componentDidMount() {
    this.loadJobs();
  }

  loadJobs = async () => {
    const { currentPage, jobsPerPage } = this.state;
    const offset = (currentPage - 1) * jobsPerPage;
    
    try {
      const data = await getJobs(jobsPerPage, offset);
      this.setState({ 
        jobs: data.jobs || [], 
        totalJobs: data.total || 0,
        loading: false 
      });
    } catch (error) {
      this.setState({ 
        error: 'Failed to load jobs',
        loading: false 
      });
    }
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page, loading: true }, () => {
      this.loadJobs();
    });
  };

  handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await deleteJob(jobId);
      this.props.showAlert('Job deleted successfully', 'success');
      this.loadJobs();
    } catch (error) {
      this.props.showAlert('Failed to delete job', 'danger');
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  getStatusBadge = (status) => {
    const variants = {
      'completed': 'success',
      'failed': 'danger',
      'processing': 'warning',
      'pending': 'secondary',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  renderPagination = () => {
    const { currentPage, totalJobs, jobsPerPage } = this.state;
    const totalPages = Math.ceil(totalJobs / jobsPerPage);
    
    if (totalPages <= 1) return null;
    
    const items = [];
    
    // First and Previous
    items.push(
      <Pagination.First 
        key="first" 
        onClick={() => this.handlePageChange(1)} 
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => this.handlePageChange(currentPage - 1)} 
        disabled={currentPage === 1}
      />
    );
    
    // Page numbers
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    
    if (startPage > 1) {
      items.push(<Pagination.Item key={1} onClick={() => this.handlePageChange(1)}>1</Pagination.Item>);
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
      }
    }
    
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item 
          key={page} 
          active={page === currentPage}
          onClick={() => this.handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => this.handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next and Last
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => this.handlePageChange(currentPage + 1)} 
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last 
        key="last" 
        onClick={() => this.handlePageChange(totalPages)} 
        disabled={currentPage === totalPages}
      />
    );
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <span className="text-muted">
          Showing {((currentPage - 1) * jobsPerPage) + 1} - {Math.min(currentPage * jobsPerPage, totalJobs)} of {totalJobs} jobs
        </span>
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  render() {
    const { jobs, loading, error, totalJobs } = this.state;

    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading jobs...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h4>{error}</h4>
        </div>
      );
    }

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Processing Jobs ({totalJobs})</h2>
          <Link to="/upload" className="btn btn-primary">
            📤 Upload New
          </Link>
        </div>

        <Card>
          <Card.Body>
            {jobs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h4>No jobs yet</h4>
                <p>Upload a file to get started</p>
                <Link to="/upload" className="btn btn-primary">
                  Upload Data
                </Link>
              </div>
            ) : (
              <>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Filename</th>
                      <th>Status</th>
                      <th>Rows</th>
                      <th>Anomalies</th>
                      <th>Issues</th>
                      <th>Time</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr key={job.id}>
                        <td>{job.id}</td>
                        <td>
                          <Link to={`/jobs/${job.id}`}>
                            {job.filename}
                          </Link>
                        </td>
                        <td>{this.getStatusBadge(job.status)}</td>
                        <td>
                          {job.original_shape?.[0] || 0} → {job.cleaned_shape?.[0] || 0}
                        </td>
                        <td>
                          {job.anomalies?.ml_count > 0 ? (
                            <Badge variant="danger">{job.anomalies.ml_count}</Badge>
                          ) : (
                            <Badge variant="success">0</Badge>
                          )}
                        </td>
                        <td>
                          {job.validation?.total_issues > 0 ? (
                            <Badge variant="warning">{job.validation.total_issues}</Badge>
                          ) : (
                            <Badge variant="success">0</Badge>
                          )}
                        </td>
                        <td>{job.processing_time_ms || 0} ms</td>
                        <td>{this.formatDate(job.created_at)}</td>
                        <td>
                          <a
                            href={`/api/download/cleaned_${job.id}_${job.filename}`}
                            className="btn btn-sm btn-outline-success mr-1"
                            download
                            title="Download cleaned file"
                          >
                            ⬇
                          </a>
                          <Link 
                            to={`/jobs/${job.id}`} 
                            className="btn btn-sm btn-outline-primary mr-1"
                          >
                            View
                          </Link>
                          <Button 
                            size="sm" 
                            variant="outline-danger"
                            onClick={() => this.handleDelete(job.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {this.renderPagination()}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default Jobs;